import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import type { DomainFishType, LiceType, LocationTypeInfo } from '@/domain/cages';
import { translateDataLabel } from '@/i18n/dataLabels';

import {
    isFishTypeChosen,
    isOptionChosen,
    type LocationFormValues,
    NO_FISH_TYPE_OPTION,
    UNSELECTED_OPTION,
} from './locationFormValues';

interface LocationTypeFieldsProps {
    register: UseFormRegister<LocationFormValues>;
    errors: FieldErrors<LocationFormValues>;
    locationTypesList: LocationTypeInfo[];
    fishTypesList: DomainFishType[];
    liceTypesList: LiceType[];
    /** Offers "No default species", for an existing location that has none. */
    allowsNoFishType: boolean;
}

/** The location, default species and lice type selects of the location dialog. */
export default function LocationTypeFields(props: LocationTypeFieldsProps) {
    const { register, errors, locationTypesList, fishTypesList, liceTypesList, allowsNoFishType } =
        props;
    const { t } = useTranslation('clientLocations');
    const { t: tLegacy } = useTranslation();

    return (
        <>
            <FormField
                label={t('addLocationModal.locationType')}
                htmlFor="locationtypeid"
                error={
                    errors.locationtypeid ? t('addLocationModal.locationTypeRequired') : undefined
                }>
                <Select {...register('locationtypeid', { validate: isOptionChosen })}>
                    <option value={UNSELECTED_OPTION}>
                        {`--- ${t('addLocationModal.chooseLocationType')} ---`}
                    </option>
                    {locationTypesList.map((locationType) => {
                        return (
                            <option key={locationType.id} value={locationType.id}>
                                {translateDataLabel(tLegacy, 'locationTypes', locationType.name)}
                            </option>
                        );
                    })}
                </Select>
            </FormField>
            <FormField
                label={t('addLocationModal.fishType')}
                htmlFor="fishtypeid"
                hint={t('addLocationModal.fishTypeHelp')}
                error={errors.fishtypeid ? t('addLocationModal.fishTypeRequired') : undefined}>
                <Select
                    {...register('fishtypeid', {
                        validate: (value) => isFishTypeChosen(value, allowsNoFishType),
                    })}>
                    <option value={UNSELECTED_OPTION}>
                        {`--- ${t('addLocationModal.chooseFishType')} ---`}
                    </option>
                    {allowsNoFishType && (
                        <option value={NO_FISH_TYPE_OPTION}>
                            {t('addLocationModal.noFishType')}
                        </option>
                    )}
                    {fishTypesList.map((fishType) => {
                        return (
                            <option key={fishType.id} value={fishType.id}>
                                {translateDataLabel(tLegacy, 'fishTypes', fishType.name)}
                            </option>
                        );
                    })}
                </Select>
            </FormField>
            <FormField
                label={t('addLocationModal.liceType')}
                htmlFor="licetypeid"
                error={errors.licetypeid ? t('addLocationModal.liceTypeRequired') : undefined}>
                <Select {...register('licetypeid', { validate: isOptionChosen })}>
                    <option value={UNSELECTED_OPTION}>
                        {`--- ${t('addLocationModal.chooseLiceType')} ---`}
                    </option>
                    {liceTypesList.map((liceType) => {
                        return (
                            <option key={liceType.id} value={liceType.id}>
                                {translateDataLabel(tLegacy, 'liceTypes', liceType.name)}
                            </option>
                        );
                    })}
                </Select>
            </FormField>
        </>
    );
}
